import React from "react";
import { isRouteErrorResponse, Link, useRouteError } from "react-router-dom";
import { Button } from "@/components/ui/button";

interface NotFoundProps {
  code?: string;
  title?: string;
  detail?: string;
  technical?: string;
}

export function NotFound({
  code = "404",
  title = "Page not found",
  detail = "That address doesn't exist. The link may be out of date, or the page may have moved.",
  technical,
}: NotFoundProps) {
  return (
    <div className="min-h-svh w-full flex items-center justify-center bg-muted text-foreground p-6">
      <div className="w-full max-w-md space-y-6">
        <div className="flex justify-center">
          <Link to="/">
            <img src="/assets/images/retreevo-logo.svg" alt="" className="h-10 w-auto" />
          </Link>
        </div>

        <div className="rounded-xl border bg-card shadow-sm p-8 space-y-6 text-center">
          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground tabular-nums">{code}</p>
            <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
            <p className="text-sm text-muted-foreground">{detail}</p>
          </div>

          {technical && <p className="break-all font-mono text-xs text-muted-foreground">{technical}</p>}

          <Button asChild className="w-full">
            <Link to="/dashboard">Go to dashboard</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

export function RouteErrorBoundary() {
  const error = useRouteError();

  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFound />;
  }

  const technical =
    error instanceof Error
      ? error.message
      : isRouteErrorResponse(error)
        ? `${error.status} ${error.statusText}`
        : undefined;

  return (
    <NotFound
      code="Error"
      title="Something went wrong"
      detail="This page failed to load. Reloading often clears it; if it keeps happening, let us know what you were doing."
      technical={technical}
    />
  );
}

export default NotFound;
